import { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { ResultsView } from "@/components/ResultsView";
import { PhotoDiagnosis } from "@/components/PhotoDiagnosis";
import { SpeakButton } from "@/components/SpeakButton";
import { SaveOfflineButton } from "@/components/SaveOfflineButton";
import { soilSpeechText } from "@/lib/speech";
import { useAuth } from "@/context/AuthContext";
import apiClient, { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { TestTube, Sparkles, Loader2, Save, Download, FileText, Languages, Camera } from "lucide-react";
import { toast } from "sonner";
import { LANGUAGES } from "@/lib/languages";
import { downloadReport } from "@/lib/report";

const soilTypes = ["Clay", "Loam", "Sandy", "Silt", "Peat", "Chalk"];
const presets = {
  "Rich Mississippi Delta Loam": { ph: 6.5, soil_type: "Loam", moisture: 55, organic_matter: 5.5, nitrogen: 70, phosphorus: 60, potassium: 65 },
  "Acidic Highland Clay": { ph: 5.0, soil_type: "Clay", moisture: 65, organic_matter: 3.0, nitrogen: 35, phosphorus: 25, potassium: 40 },
  "Dry Sandy Scrubland": { ph: 7.8, soil_type: "Sandy", moisture: 15, organic_matter: 1.0, nitrogen: 20, phosphorus: 30, potassium: 25 },
};

const defaultSoil = { ph: 6.5, soil_type: "Loam", moisture: 40, organic_matter: 3.0, nitrogen: 40, phosphorus: 30, potassium: 35 };

function NumSlider({ label, unit, value, min, max, step, onChange, testid, color }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">{label}</Label>
        <span className="font-mono-tech text-sm font-bold" style={{ color }}>{value}{unit}</span>
      </div>
      <Slider data-testid={testid} value={[value]} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0])} />
    </div>
  );
}

export default function Analyze() {
  const { user, updateLanguage } = useAuth();
  const [soil, setSoil] = useState(defaultSoil);
  const [meta, setMeta] = useState({ location: "", goal: "" });
  const [language, setLanguage] = useState(user?.language || "English");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [tab, setTab] = useState("manual");
  const [saveOpen, setSaveOpen] = useState(false);
  const [fieldName, setFieldName] = useState("");
  const [lastSoil, setLastSoil] = useState(defaultSoil);

  const s = (k) => (v) => setSoil({ ...soil, [k]: v });

  const changeLanguage = (l) => { setLanguage(l); updateLanguage(l); };

  const showResult = (data) => {
    setResult(data);
    setLastSoil(data.soil || soil);
    setTimeout(() => document.getElementById("results-anchor")?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const applyEstimate = (est) => {
    const clamp = (v, lo, hi, d) => (typeof v === "number" && !isNaN(v) ? Math.min(hi, Math.max(lo, v)) : d);
    const matched = soilTypes.find((t) => String(est?.soil_type || "").toLowerCase().includes(t.toLowerCase()));
    setSoil({
      ph: +clamp(est?.ph, 4, 9, soil.ph).toFixed(1),
      soil_type: matched || soil.soil_type,
      moisture: Math.round(clamp(est?.moisture, 0, 100, soil.moisture)),
      organic_matter: +clamp(est?.organic_matter, 0, 15, soil.organic_matter).toFixed(1),
      nitrogen: Math.round(clamp(est?.nitrogen, 0, 100, soil.nitrogen)),
      phosphorus: Math.round(clamp(est?.phosphorus, 0, 100, soil.phosphorus)),
      potassium: Math.round(clamp(est?.potassium, 0, 100, soil.potassium)),
    });
    setTab("manual");
  };

  const applyPreset = (name) => {
    setSoil(presets[name]);
    toast.success(`Loaded preset: ${name}`);
  };

  const runAnalysis = async (mode) => {
    setLoading(true);
    setResult(null);
    try {
      const body = mode === "ai"
        ? { mode: "ai", description, location: meta.location, goal: meta.goal, language }
        : { mode: "manual", soil, location: meta.location, goal: meta.goal, language };
      const res = await apiClient.post("/analyze", body);
      showResult(res.data);
      toast.success("Analysis complete!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const saveField = async () => {
    if (!fieldName.trim()) return toast.error("Give your field a name");
    try {
      await apiClient.post("/fields", { name: fieldName, location: meta.location, soil: lastSoil, analysis: result });
      toast.success("Field saved!");
      setSaveOpen(false);
      setFieldName("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify({ soil: lastSoil, analysis: result }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "agroterra-analysis.json"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-emerald-950 tracking-tight mb-2">Soil Diagnostic</h1>
        <p className="text-stone-600 mb-6">Enter test values, snap a photo, or just describe your land — AgroTerra AI does the rest.</p>

        <div className="flex items-center gap-3 mb-8 bg-white border border-stone-200 rounded-xl px-4 py-3 w-fit">
          <Languages className="h-4 w-4 text-[#2D6A4F]" />
          <span className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Advice language</span>
          <Select value={language} onValueChange={changeLanguage}>
            <SelectTrigger data-testid="language-select" className="w-[160px] h-9 bg-white border-stone-200"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mb-8">
          <TabsList className="bg-white border border-stone-200 p-1 rounded-xl h-auto flex-wrap">
            <TabsTrigger value="manual" data-testid="tab-manual" className="rounded-lg data-[state=active]:bg-[#2D6A4F] data-[state=active]:text-white px-4 py-2">
              <TestTube className="h-4 w-4 mr-2" /> Manual Input
            </TabsTrigger>
            <TabsTrigger value="photo" data-testid="tab-photo" className="rounded-lg data-[state=active]:bg-[#2D6A4F] data-[state=active]:text-white px-4 py-2">
              <Camera className="h-4 w-4 mr-2" /> Soil Photo
            </TabsTrigger>
            <TabsTrigger value="ai" data-testid="tab-ai" className="rounded-lg data-[state=active]:bg-[#2D6A4F] data-[state=active]:text-white px-4 py-2">
              <Sparkles className="h-4 w-4 mr-2" /> AI Soil Story
            </TabsTrigger>
          </TabsList>

          {/* Manual */}
          <TabsContent value="manual" className="mt-6">
            <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8">
              <div className="flex flex-wrap gap-2 mb-6">
                <span className="text-xs uppercase tracking-wider text-stone-500 font-semibold self-center mr-1">Quick presets:</span>
                {Object.keys(presets).map((p) => (
                  <button key={p} data-testid={`preset-${p.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => applyPreset(p)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-full bg-[#EAF6EF] text-[#2D6A4F] hover:bg-[#52B788] hover:text-emerald-950 transition-colors">
                    {p}
                  </button>
                ))}
              </div>

              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
                <NumSlider label="pH Level" unit="" value={soil.ph} min={4} max={9} step={0.1} onChange={s("ph")} testid="soil-ph-slider" color="#8B5CF6" />
                <div>
                  <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2 block">Soil Type</Label>
                  <Select value={soil.soil_type} onValueChange={s("soil_type")}>
                    <SelectTrigger data-testid="soil-type-select" className="bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {soilTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <NumSlider label="Moisture" unit="%" value={soil.moisture} min={0} max={100} step={1} onChange={s("moisture")} testid="soil-moisture-slider" color="#3B82F6" />
                <NumSlider label="Organic Matter" unit="%" value={soil.organic_matter} min={0} max={15} step={0.1} onChange={s("organic_matter")} testid="soil-organic-slider" color="#84CC16" />
                <NumSlider label="Nitrogen (N)" unit="/100" value={soil.nitrogen} min={0} max={100} step={1} onChange={s("nitrogen")} testid="soil-n-slider" color="#22C55E" />
                <NumSlider label="Phosphorus (P)" unit="/100" value={soil.phosphorus} min={0} max={100} step={1} onChange={s("phosphorus")} testid="soil-p-slider" color="#F59E0B" />
                <NumSlider label="Potassium (K)" unit="/100" value={soil.potassium} min={0} max={100} step={1} onChange={s("potassium")} testid="soil-k-slider" color="#3B82F6" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mt-6 pt-6 border-t border-stone-100">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Location (optional)</Label>
                  <Input data-testid="location-input" value={meta.location} onChange={(e) => setMeta({ ...meta, location: e.target.value })} placeholder="e.g. Punjab, India" className="mt-1.5 bg-white" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Goal / notes (optional)</Label>
                  <Input data-testid="goal-input" value={meta.goal} onChange={(e) => setMeta({ ...meta, goal: e.target.value })} placeholder="e.g. want a profitable cash crop" className="mt-1.5 bg-white" />
                </div>
              </div>

              <Button data-testid="analyze-soil-btn" onClick={() => runAnalysis("manual")} disabled={loading}
                className="mt-6 w-full sm:w-auto h-12 px-8 bg-[#D97706] hover:bg-[#78350F] text-white rounded-xl text-base font-semibold">
                {loading ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Analysing…</> : <><TestTube className="h-5 w-5 mr-2" /> Analyse Soil</>}
              </Button>
            </div>
          </TabsContent>

          {/* Photo */}
          <TabsContent value="photo" className="mt-6">
            <PhotoDiagnosis meta={meta} setMeta={setMeta} language={language} onEstimate={applyEstimate} onFullResult={showResult} />
          </TabsContent>

          {/* AI */}
          <TabsContent value="ai" className="mt-6">
            <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8">
              <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Describe your land</Label>
              <Textarea data-testid="ai-description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={5}
                placeholder="e.g. Red clay soil that stays wet for days after rain. Corn did poorly last year and the ground cracks when it dries. Near a river in a hot region."
                className="mt-1.5 bg-white resize-none" />
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Location (optional)</Label>
                  <Input data-testid="ai-location-input" value={meta.location} onChange={(e) => setMeta({ ...meta, location: e.target.value })} placeholder="e.g. Kenya" className="mt-1.5 bg-white" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Goal / notes (optional)</Label>
                  <Input data-testid="ai-goal-input" value={meta.goal} onChange={(e) => setMeta({ ...meta, goal: e.target.value })} placeholder="e.g. improve soil for vegetables" className="mt-1.5 bg-white" />
                </div>
              </div>
              <Button data-testid="ai-analyze-btn" onClick={() => runAnalysis("ai")} disabled={loading || !description.trim()}
                className="mt-6 w-full sm:w-auto h-12 px-8 bg-[#2D6A4F] hover:bg-[#143D29] text-white rounded-xl text-base font-semibold">
                {loading ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Thinking…</> : <><Sparkles className="h-5 w-5 mr-2" /> Analyse with AI</>}
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        <div id="results-anchor" />
        {result && (
          <div className="space-y-6">
            <div className="flex flex-wrap gap-3 justify-end">
              <SpeakButton text={soilSpeechText(result, meta.location || null)} language={language} prefetch />
              <SaveOfflineButton item={{ id: `soil-${result.soil_health?.score}-${(result.crops || []).map((c) => c.name).join("").slice(0, 20)}`, kind: "soil", title: meta.location ? `Soil report — ${meta.location}` : "Soil report", subtitle: `Score ${Math.round(result.soil_health?.score || 0)} · ${lastSoil?.soil_type || ""}`, payload: { location: meta.location, soil: lastSoil, analysis: result } }} />
              <Button data-testid="save-field-btn" onClick={() => setSaveOpen(true)} variant="outline" className="border-stone-300">
                <Save className="h-4 w-4 mr-2" /> Save as field
              </Button>
              <Button data-testid="download-pdf-btn" onClick={() => downloadReport({ name: "Soil Analysis", location: meta.location, soil: lastSoil, analysis: result })} variant="outline" className="border-stone-300">
                <FileText className="h-4 w-4 mr-2" /> Download PDF
              </Button>
              <Button data-testid="export-json-btn" onClick={exportJson} variant="outline" className="border-stone-300">
                <Download className="h-4 w-4 mr-2" /> Export JSON
              </Button>
            </div>
            <ResultsView analysis={result} />
          </div>
        )}
      </main>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent data-testid="save-field-dialog">
          <DialogHeader><DialogTitle className="font-display">Save this field</DialogTitle></DialogHeader>
          <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Field name</Label>
          <Input data-testid="field-name-input" value={fieldName} onChange={(e) => setFieldName(e.target.value)} placeholder="e.g. North Paddock" className="bg-white" />
          <DialogFooter>
            <Button data-testid="confirm-save-field-btn" onClick={saveField} className="bg-[#2D6A4F] hover:bg-[#143D29] text-white">Save field</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
